import {parseInteger} from '@benchling/js-utils';
import React, {useCallback, useState} from 'react';

import {ReorderOptions} from 'notebook/tables/excess/ops/TableOps';
import {TableDimension} from 'notebook/tables/TableConstants';

export interface UseSingleInputDragHandlersOptions {
  numSingleInputs: number;
  canReorder: boolean;
  tableIndex?: number;
  storeState?: () => void;
  reorderSingleInputs?: (tableIndex: number, options: ReorderOptions) => void;
}

export interface SingleInputDragProps {
  onDragStart: (index: number) => void;
  onDragOver: (index: number) => void;
  onDragEnd: () => void;
  canDrag: boolean;
}

export interface DropZoneDragProps {
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnter: (e: React.DragEvent<HTMLDivElement>) => void;
}

export interface StopDragPropagationProps {
  onDragStart: (e: React.DragEvent) => void;
  onDrag: (e: React.DragEvent) => void;
  onDragEnter: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: (e: React.DragEvent) => void;
}

const stopDragPropagation = (e: React.DragEvent): void => {
  e.preventDefault();
  e.stopPropagation();
};

export function getStopDragPropagationProps(): StopDragPropagationProps {
  return {
    onDragStart: stopDragPropagation,
    onDrag: stopDragPropagation,
    onDragEnter: stopDragPropagation,
    onDragOver: stopDragPropagation,
    onDragLeave: stopDragPropagation,
    onDrop: stopDragPropagation,
    onDragEnd: stopDragPropagation,
  };
}

export interface SingleInputDragState {
  dragSourceIndex: number | null;
  dragOverIndex: number | null;
  singleInputDragProps: SingleInputDragProps;
  dropZoneDragProps: DropZoneDragProps;
}

export default function useSingleInputDragHandlers({
  numSingleInputs,
  canReorder,
  tableIndex,
  storeState,
  reorderSingleInputs,
}: UseSingleInputDragHandlersOptions): SingleInputDragState {
  const [dragSourceIndex, setDragSourceIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const canDrag = numSingleInputs > 1 && canReorder && tableIndex != null && reorderSingleInputs != null;

  const handleDragStart = useCallback((index: number): void => {
    setDragSourceIndex(index);
    setDragOverIndex(null);
  }, []);

  const handleDragOver = useCallback((index: number): void => {
    setDragOverIndex(index);
  }, []);

  const handleDragEnd = useCallback((): void => {
    if (
      dragSourceIndex != null &&
      dragOverIndex != null &&
      dragSourceIndex !== dragOverIndex &&
      tableIndex != null &&
      reorderSingleInputs != null
    ) {
      storeState?.();

      // Drop zones use offset indices [numSingleInputs, 2*numSingleInputs-1] to distinguish
      // from input indices [0, numSingleInputs-1]. Example: with 3 inputs, inputs are [0,1,2]
      // and drop zones are [3,4,5].
      const isDropZone = dragOverIndex >= numSingleInputs;
      const insertPosition = isDropZone
        ? dragOverIndex - numSingleInputs + 1 // Drop zone after input i -> insert after i
        : dragOverIndex; // Hovering over input i -> insert before i

      reorderSingleInputs(tableIndex, {
        dim: TableDimension.COLUMN,
        startIndex: dragSourceIndex,
        endIndex: dragSourceIndex,
        insertPosition,
      });
    }
    setDragSourceIndex(null);
    setDragOverIndex(null);
  }, [dragSourceIndex, dragOverIndex, tableIndex, storeState, reorderSingleInputs, numSingleInputs]);

  const handleDropZoneDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      e.stopPropagation();
      const dropZoneIndex = parseInteger(e.currentTarget.dataset.dropZoneIndex ?? '');
      if (dropZoneIndex != null) {
        handleDragOver(dropZoneIndex);
      }
    },
    [handleDragOver]
  );

  return {
    dragSourceIndex,
    dragOverIndex,
    singleInputDragProps: {
      onDragStart: handleDragStart,
      onDragOver: handleDragOver,
      onDragEnd: handleDragEnd,
      canDrag,
    },
    dropZoneDragProps: {
      onDragOver: handleDropZoneDragOver,
      onDragEnter: handleDropZoneDragOver,
    },
  };
}
